import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { PageFrame } from '@/shared/components/ui/PageFrame';
import { MiniappSurface } from '@/shared/components/ui/MiniappSurface';
import { StackHeader } from '@/shared/components/ui/StackHeader';
import {
  buildLegalHref,
  openSupportEmail,
  resolveLegalReturnTo,
} from '@/features/legal/legal-navigation';

function DocumentRow({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start justify-between gap-3 rounded-[20px] border border-[rgba(26,26,26,0.06)] bg-white/76 px-4 py-4 text-left transition hover:border-[rgba(26,26,26,0.14)]"
    >
      <div className="min-w-0 space-y-1">
        <p className="text-[15px] font-semibold text-[var(--foreground)]">{title}</p>
        <p className="text-[13px] leading-5 text-[rgba(26,26,26,0.58)]">{description}</p>
      </div>
      <span className="pt-1 text-[18px] text-[rgba(26,26,26,0.42)]">›</span>
    </button>
  );
}

export function LegalHubPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = resolveLegalReturnTo(location.search, '/catalog');

  const handleBack = useCallback(() => {
    void navigate(returnTo);
  }, [navigate, returnTo]);

  return (
    <PageFrame
      container="readable-content"
      topInset="stack"
      bottomInset="none"
      className="space-y-4 pb-6"
    >
      <StackHeader onBack={handleBack} label="Документы" title="Юридическая информация" />

      <MiniappSurface className="space-y-3 px-5 py-5">
        <div className="space-y-3">
          <h1 className="text-[22px] font-black uppercase leading-[0.95] tracking-[0.02em] text-[var(--foreground)]">
            ДОКУМЕНТЫ
          </h1>
          <div
            aria-label="Реквизиты"
            className="space-y-1 text-[14px] leading-6 text-[rgba(26,26,26,0.72)]"
          >
            <p>ИП Анников Павел Андреевич</p>
            <p>ИНН: 561202861214</p>
            <p>ОГРН/ОГРНИП: 319565800069512</p>
            <p>Контактный e-mail: info@inhound.club</p>
          </div>
        </div>

        <div className="space-y-3">
          <DocumentRow
            title="Публичная оферта"
            description="Условия оказания услуг и порядок расчётов."
            onClick={() => void navigate(buildLegalHref('/legal/offer', returnTo))}
          />
          <DocumentRow
            title="Порядок оказания услуг, оплаты и возврата"
            description="Когда открывается доступ после оплаты и как подать заявку на возврат."
            onClick={() => void navigate(buildLegalHref('/legal/payment', returnTo))}
          />
          <DocumentRow
            title="Политика обработки персональных данных"
            description="Порядок обработки, хранения и защиты персональных данных."
            onClick={() => void navigate(buildLegalHref('/legal/privacy', returnTo))}
          />
          <DocumentRow
            title="Вопросы по документам"
            description="Написать на info@inhound.club."
            onClick={() => openSupportEmail('Вопрос по юридическим документам')}
          />
        </div>
      </MiniappSurface>
    </PageFrame>
  );
}
